import React, { useEffect } from "react";
import { Box, CssBaseline, ThemeProvider } from "@mui/material";
import { CacheProvider } from "@emotion/react";
import { AppProvider, useAppContext } from "./context/AppContext";
import { createDirectionalTheme, getCacheForDirection } from "./theme/rtlTheme";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import DistributorCodeHandler from "./pages/distributor/DistributorCodeHandler";

import ErrorBoundary from "./components/ErrorBoundary";
import NotFoundPage from "./components/NotFoundPage";
import CustomerFlow from "./flows/CustomerFlow";
import DistributorFlow from "./flows/DistributorFlow";
import SSOLoginRedirect from "./pages/SSOLoginRedirect";
import PrivacyPoliciesPage from "./pages/PrivacyPoliciesPage";
import { updatePwaManifestForRoute } from "./utils/pwaInstall";
import { useLocation } from "react-router-dom";
import PwaInstallPrompt from "./components/PwaInstallPrompt";
import NetworkGate from "./components/NetworkGate";

const PwaManifestSync: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    updatePwaManifestForRoute(location.pathname);
  }, [location.pathname]);

  return null;
};

// Wrapper component to access context and apply RTL theme
const ThemedApp: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { direction } = useAppContext();
  
  // Create theme and cache based on direction
  const theme = React.useMemo(() => createDirectionalTheme(direction), [direction]);
  const cache = React.useMemo(() => getCacheForDirection(direction), [direction]);
  
  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </CacheProvider>
  );
};

const App: React.FC = () => {
  // PWA/bfcache return: reload so a restored page does not keep a stale session.
  // useEffect(() => {
  //   const handlePageShow = (event: PageTransitionEvent) => {
  //     if (event.persisted) {
  //       window.location.reload();
  //     }
  //   };
  //
  //   window.addEventListener("pageshow", handlePageShow);
  //
  //   return () => {
  //     window.removeEventListener("pageshow", handlePageShow);
  //   };
  // }, []);

  return (
    <ErrorBoundary>
      <Router>
        <NetworkGate>
            <PwaManifestSync />
            <PwaInstallPrompt />
            <Routes>
          <Route path="/" element={<Navigate to="/distributor" replace />} />

          <Route path="/sso-login/*" element={<SSOLoginRedirect />} />

          <Route
            path="/privacy-policy"
            element={
              <AppProvider>
                <ThemedApp>
                  <Box
                    sx={{
                      width: "100%",
                      minHeight: "100dvh",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    <PrivacyPoliciesPage />
                  </Box>
                </ThemedApp>
              </AppProvider>
            }
          />

          {/* Distributor routes - wrapped with AppProvider and ThemedApp */}
          <Route
            path="/distributor/*"
            element={
              <AppProvider>
                <ThemedApp>
                  <Routes>
                    <Route 
                      path="successful" 
                      element={
                        <Box
                          sx={{
                            width: "100%",
                            minHeight: "100dvh",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                          }}
                        >
                          <DistributorCodeHandler />
                        </Box>
                      } 
                    />
                    <Route
                      path="*"
                      element={
                        <Box
                          sx={{
                            width: "100%",
                            minHeight: "100dvh",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                          }}
                        >
                          <DistributorFlow />
                        </Box>
                      }
                    />
                  </Routes>
                </ThemedApp>
              </AppProvider>
            }
          />
          
          {/* Customer routes - wrapped with AppProvider and ThemedApp */}
          <Route
            path="/customer/*"
            element={
              <AppProvider>
                <ThemedApp>
                  <Box
                    sx={{
                      width: "100%",
                      minHeight: "100dvh",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    <CustomerFlow />
                  </Box>
                </ThemedApp>
              </AppProvider>
            }
          />
          
          <Route path="/*" element={<NotFoundPage />} />
            </Routes>
        </NetworkGate>
      </Router>
    </ErrorBoundary>
  );
};



export default App;
